import { Fragment, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Stack } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { BASIC } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { capitalize } from '@hotjar/insights-common/lib/string';

import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { trafficCoverageLevelToShortI18nKeyMap } from '@hotjar/webapp/domains/revenue/sampling/constants';

import { getFeatureGroupsGreaterThanOrEqualTo } from './utils';
import { FeatureBarStyles as S } from './styles';
import type { PlanComparisonFeatureGateDetails } from './types';

interface Props {
  featureGate: PlanComparisonFeatureGateDetails | null | undefined;
}

export const FeatureBar = ({ featureGate }: Props): JSX.Element => {
  const { t } = useTranslationWithPrefix('revenue:plan-comparison-modal');
  const { t: tCommon } = useTranslation();

  const { title, description, illustration, minimumFeatureGroup, isPlanAgnostic, modalControls } =
    featureGate ?? {};
  const { coverageLevel } = useTrafficCoverage();
  const featureGroups = useMemo(
    () => getFeatureGroupsGreaterThanOrEqualTo(minimumFeatureGroup ?? BASIC),
    [minimumFeatureGroup],
  );

  /**
   * We provide these values to the title and description translation strings, so that they can interpolate
   * these values into the translations.
   *
   * For example, you could declare a translation string as: `You have {{trafficCoverage}} traffic coverage.`.
   * And then if we declare a `trafficCoverage` property in the `i18nContext`, it can interpolate it automatically.
   *
   * Translation strings don't have to use all values available. Unused values are simply ignored.
   */
  const i18nContext = {
    trafficCoverage: tCommon(trafficCoverageLevelToShortI18nKeyMap[coverageLevel]),
  };

  const titleTranslated = title ? t(title, { ...i18nContext }) : '';

  return (
    <Stack direction="row" spacing="x7pt5">
      <div>
        <S.Illustration src={illustration} alt={titleTranslated} />
      </div>
      <S.ContentContainer>
        <Stack direction="column" spacing="x2pt5">
          <div>
            <S.FeatureTitle>{titleTranslated}</S.FeatureTitle>
            <S.FeatureDescription>
              {description && (
                <Trans
                  i18nKey={`revenue:plan-comparison-modal.${description}`}
                  tOptions={{ ...i18nContext }}
                  components={[<S.ListItem />]}
                />
              )}
            </S.FeatureDescription>
          </div>

          {!modalControls?.hideFeatureBarPlans && !isPlanAgnostic && (
            <S.FeaturePlanListLabel>
              {t('available-on')}{' '}
              {featureGroups.map((featureGroup, index) => (
                <Fragment key={featureGroup}>
                  {featureGroups.length > 1 &&
                    index !== 0 &&
                    index < featureGroups.length - 1 &&
                    `, `}
                  {featureGroups.length > 1 &&
                    index === featureGroups.length - 1 &&
                    ` ${t('and')} `}
                  <strong>{capitalize(featureGroup)}</strong>
                </Fragment>
              ))}{' '}
              {t('plans', { count: featureGroups.length })}
            </S.FeaturePlanListLabel>
          )}
        </Stack>
      </S.ContentContainer>
    </Stack>
  );
};
